import { API_AGENCY_ONBOARDING_FORM, API_CONSULTANT_ONBOARDING_FORM } from "../../../../../config/Endpoints.js";
import { postFormDataMultipart } from "../../../../utils/api.ts";

export type SubmissionValues = Record<string, string | null>;
export type SubmissionFiles = Partial<Record<string, File>>;

// Only values confirmed by the API contract belong here. Unknown uploads must
// never be silently omitted or sent with an invented document type.
export const DOCUMENT_TYPES: Readonly<Record<string, string>> = {
  tax: "W9_W8",
  taxForm: "W9_W8",
  nda: "NDA_CONFIDENTIALITY",
  confidentiality: "NDA_CONFIDENTIALITY",
  agreement: "INDEPENDENT_CONTRACTOR_AGREEMENT",
  vendorAgreement: "MSA_VENDOR_AGREEMENT",
  engagementAuthorization: "SOW_PO",
  insurance: "CERTIFICATE_OF_INSURANCE",
  businessLicense: "BUSINESS_LICENSE",
  businessLicenseCredential: "BUSINESS_LICENSE_CREDENTIAL",
  certification: "PROFESSIONAL_CERTIFICATION_LICENSE",
  agencyResponsibility: "AGENCY_AGREEMENT",
  screening: "BACKGROUND_SCREENING",
  consultantProfile: "CONSULTANT_RESUME_PROFILE",
  client: "CLIENT_SPECIFIC_COMPLIANCE",
  clientCompliance: "CLIENT_SPECIFIC_COMPLIANCE",
  cheque: "CANCELLED_CHEQUE",
};

export function buildSubmissionData(
  values: SubmissionValues,
  files: SubmissionFiles,
) {
  const attachments = Object.entries(files).filter(
    (entry): entry is [string, File] => Boolean(entry[1]),
  );

  const documentTypes = attachments.map(([key, file]) => {
    if (!DOCUMENT_TYPES[key]) {
      throw new Error(
        `The document type for ${file.name} is not configured. Please contact HR.`,
      );
    }

    if (!file.size) {
      throw new Error(
        `${file.name} is empty. Please select a completed document.`,
      );
    }

    return DOCUMENT_TYPES[key];
  });

if (
  values.consultantType !== "INDIAN_CONSULTANT" &&
  !documentTypes.includes("W9_W8")
) {
  throw new Error(
    "Please upload your completed and signed W-9 or applicable W-8.",
  );
}

  const body = new FormData();

  body.append(
    "data",
    new Blob(
      [JSON.stringify({ ...values, documentTypes })],
      { type: "application/json" },
    ),
  );

  attachments.forEach(([, file]) => body.append("files", file));

  return body;
}

export async function submitOnboardingForm(
  recipient: "consultant" | "agency",
  token: string,
  values: SubmissionValues,
  files: SubmissionFiles,
) {
  if (!token) {
    throw new Error(
      "Open your invitation link and verify your email before submitting.",
    );
  }

  const endpoint =
    recipient === "agency"
      ? API_AGENCY_ONBOARDING_FORM
      : API_CONSULTANT_ONBOARDING_FORM;

  return postFormDataMultipart<{ message?: string; readyForIt?: boolean }>(
    `${endpoint}?${new URLSearchParams({ token })}`,
    undefined,
    buildSubmissionData(values, files),
  );
}

export const optional = (value: string) => value.trim() || null;
export const cityStateZip = (values: { city: string; state: string; zip: string }) => [values.city, values.state, values.zip].map((value) => value.trim()).filter(Boolean).join(", ") || null;
export const businessType = (value: string) =>
  ({
    individual: "INDIVIDUAL_SOLE_PROPRIETOR",
    llc: "LLC",
    corporation: "CORPORATION",
    partnership: "PARTNERSHIP",
    other: "OTHER",
  }[value] ?? null);export const paymentMethod = (value: string) => ({ ach: "ACH_DIRECT_DEPOSIT", wire: "WIRE_TRANSFER", check: "CHECK" }[value] ?? null);
export const paymentTerms = (value: string) => ({ net15: "NET_15", net30: "NET_30", net45: "NET_45", other: "OTHER" }[value] ?? null);
